import { useId, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { RelativeTime } from "@/components/admin/RelativeTime";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  useUpdateSetting,
  type AdminSetting,
  type SettingValue,
} from "@/queries/adminSettings";

import { ReplaceSecretDialog } from "./ReplaceSecretDialog";
import { RequiresRestartBadge } from "./RequiresRestartBadge";
import { SettingValueInput } from "./SettingValueInput";
import { schemaForSetting, type SettingFormValues } from "./settingSchema";

interface SettingRowProps {
  setting: AdminSetting;
}

export function SettingRow({ setting }: SettingRowProps) {
  const { t } = useTranslation("admin-settings");
  const [isEditing, setIsEditing] = useState(false);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutation = useUpdateSetting(setting.key);
  const inputId = useId();

  const form = useForm<SettingFormValues>({
    resolver: zodResolver(schemaForSetting(setting)),
    defaultValues: { value: setting.value },
    values: { value: setting.value },
  });

  const onCancel = () => {
    form.reset({ value: setting.value });
    setSubmitError(null);
    setIsEditing(false);
  };

  const onSubmit = (values: SettingFormValues) => {
    setSubmitError(null);
    const payload = normalizeForSubmit(values.value, setting);
    mutation.mutate(
      { value: payload },
      {
        onSuccess: () => setIsEditing(false),
        onError: () => setSubmitError(t("errors.saveGeneric")),
      },
    );
  };

  const fieldError = form.formState.errors.value?.message;
  const translatedFieldError =
    typeof fieldError === "string" && fieldError.startsWith("errors.")
      ? t(fieldError)
      : (fieldError ?? null);

  return (
    <li className="flex flex-col gap-3 border-b py-4 last:border-b-0">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono">
            {setting.key}
          </code>
          {setting.requires_restart ? <RequiresRestartBadge /> : null}
        </div>
        {setting.description ? (
          <p className="text-xs text-muted-foreground">{setting.description}</p>
        ) : null}
      </div>

      {isEditing ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor={inputId} className="sr-only">
              {setting.key}
            </Label>
            <SettingValueInput
              setting={setting}
              control={form.control}
              inputId={inputId}
              disabled={mutation.isPending}
            />
            {translatedFieldError ? (
              <p className="text-xs text-destructive" role="alert">
                {translatedFieldError}
              </p>
            ) : null}
          </div>

          {submitError ? (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={mutation.isPending}>
              {mutation.isPending ? t("row.saving") : t("row.save")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onCancel}
              disabled={mutation.isPending}
            >
              {t("row.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ValueDisplay setting={setting} />
          {setting.is_secret ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSecretDialogOpen(true)}
              data-testid={`replace-${setting.key}`}
            >
              {t("row.replace")}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(true)}
              data-testid={`edit-${setting.key}`}
            >
              {t("row.edit")}
            </Button>
          )}
        </div>
      )}

      <AuditLine setting={setting} />

      {setting.is_secret ? (
        <ReplaceSecretDialog
          setting={setting}
          open={secretDialogOpen}
          onOpenChange={setSecretDialogOpen}
        />
      ) : null}
    </li>
  );
}

function ValueDisplay({ setting }: { setting: AdminSetting }) {
  const { t } = useTranslation("admin-settings");
  if (setting.is_secret) {
    return (
      <span className="font-mono text-sm">
        {setting.has_value ? t("row.secretMask") : (
          <span className="italic text-muted-foreground">{t("row.secretUnset")}</span>
        )}
      </span>
    );
  }
  if (setting.value === null || setting.value === undefined) {
    return (
      <span className="italic text-sm text-muted-foreground">
        {t("row.valueUnset")}
      </span>
    );
  }
  if (typeof setting.value === "boolean") {
    return <span className="text-sm">{String(setting.value)}</span>;
  }
  return <span className="font-mono text-sm">{String(setting.value)}</span>;
}

function AuditLine({ setting }: { setting: AdminSetting }) {
  const { t } = useTranslation("admin-settings");
  if (!setting.updated_at) {
    return <p className="text-xs text-muted-foreground">{t("row.neverChanged")}</p>;
  }
  const name = setting.updated_by_display_name ?? t("row.unknownUser");
  const link = setting.updated_by_user_id ? (
    <Link
      to="/admin/users/$id"
      params={{ id: setting.updated_by_user_id }}
      className="underline-offset-2 hover:underline"
      data-testid={`audit-link-${setting.key}`}
    >
      {name}
    </Link>
  ) : (
    <span>{name}</span>
  );

  return (
    <p className="text-xs text-muted-foreground">
      {t("row.changedByPrefix")}
      {link} ·{" "}
      <RelativeTime
        iso={setting.updated_at}
        fallbackKey="row.neverChanged"
        fallbackNs="admin-settings"
      />
    </p>
  );
}

function normalizeForSubmit(value: SettingValue, setting: AdminSetting): SettingValue {
  if (setting.value_type === "decimal" && typeof value === "number") {
    return String(value);
  }
  if (setting.value_type === "str" && typeof value === "string") {
    return value.trim() === "" ? null : value;
  }
  return value;
}
